// Floating progress panel that shows the current upload queue. Sits
// fixed in the bottom-right corner above the Sonner toaster. Auto-
// hides when there are no tasks. Starts collapsed (header strip
// only); clicking the header expands it to reveal per-file rows. A
// "Clear finished" button (visible when expanded) dismisses every
// row that has reached a terminal state (done, skipped, or error);
// each of those rows also carries a per-row × for targeted dismissal.

import { useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useUploadQueue, type UploadTask } from "../../stores/uploadQueue";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";

function pct(task: UploadTask): number {
  if (task.totalBytes <= 0) return task.status === "done" ? 100 : 0;
  return Math.max(
    0,
    Math.min(100, Math.round((task.bytesDone / task.totalBytes) * 100)),
  );
}

function statusLabel(task: UploadTask): string {
  switch (task.status) {
    case "queued":
      return "queued";
    case "running":
      return "uploading";
    case "done":
      return "done";
    case "skipped":
      return "skipped";
    case "error":
      return "error";
  }
}

function percentLabel(task: UploadTask): string {
  if (task.status === "queued") return "—";
  if (task.status === "error") return "!";
  if (task.status === "skipped") return "—";
  return `${pct(task)}%`;
}

function isTerminal(task: UploadTask): boolean {
  return (
    task.status === "done" ||
    task.status === "error" ||
    task.status === "skipped"
  );
}

function headerText(list: UploadTask[]): string {
  const total = list.length;
  const terminal = list.filter(isTerminal).length;
  const active = list.some(
    (t) => t.status === "running" || t.status === "queued",
  );
  if (!active) return `Transfers · ${terminal} of ${total}`;
  return `Encrypting & uploading · ${terminal} of ${total}`;
}

export function UploadProgress() {
  const tasks = useUploadQueue((s) => s.tasks);
  const clearDone = useUploadQueue((s) => s.clearDone);
  const removeTask = useUploadQueue((s) => s.remove);
  const list = Object.values(tasks);
  // Default collapsed: the panel only summarises a background activity.
  // It expands when the user wants per-file detail, and resets to
  // collapsed naturally when the panel unmounts (queue fully cleared).
  const [expanded, setExpanded] = useState(false);

  if (list.length === 0) return null;

  const anyFinished = list.some(isTerminal);
  const Chevron = expanded ? ChevronDown : ChevronUp;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-20 z-40 rounded-lg border border-border bg-bg-raised shadow-lg md:inset-x-auto md:bottom-4 md:right-4 md:w-80"
    >
      <div className="flex items-center gap-1 border-b border-border-soft pl-1 pr-3">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? "Minimize transfers" : "Expand transfers"}
          className="flex flex-1 items-center gap-1.5 rounded py-1.5 pl-2 pr-1 text-left hover:bg-accent-bg"
        >
          <Chevron className="h-3 w-3 text-text-dim" aria-hidden />
          <span className="flex-1 text-xs font-medium text-text-soft">
            {headerText(list)}
          </span>
        </button>
        {expanded && anyFinished && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-1.5 text-[11px]"
            onClick={clearDone}
          >
            Clear finished
          </Button>
        )}
      </div>
      {expanded && (
        <ul className="max-h-80 divide-y divide-border-soft overflow-auto">
          {list.map((task) => {
            const finished = isTerminal(task);
            const percentClass =
              task.status === "error"
                ? "text-danger"
                : task.status === "done"
                  ? "text-ok"
                  : "text-text-dim";
            const nameClass =
              task.status === "skipped" ? "text-text-dim" : "text-text";
            const ext =
              (task.fileName.split(".").pop() ?? "")
                .slice(0, 3)
                .toUpperCase() || "FILE";
            return (
              <li key={task.taskId} className="px-3 py-1.5">
                <div className="flex items-center gap-2 text-[12px] leading-tight">
                  {task.thumbnailUrl ? (
                    <img
                      src={task.thumbnailUrl}
                      alt=""
                      aria-hidden
                      className="h-5 w-5 flex-shrink-0 rounded-sm object-cover"
                    />
                  ) : (
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm bg-bg-sidebar text-[8px] font-mono text-text-dim">
                      {ext}
                    </div>
                  )}
                  <span
                    className={`flex-1 truncate ${nameClass}`}
                    title={task.fileName}
                  >
                    <span className="font-medium">{task.fileName}</span>
                    <span className="text-text-dim">
                      {" "}
                      · {statusLabel(task)}
                    </span>
                  </span>
                  <span
                    className={`w-8 text-right font-mono text-[11px] tabular-nums ${percentClass}`}
                  >
                    {percentLabel(task)}
                  </span>
                  {finished && (
                    <button
                      type="button"
                      aria-label={`Dismiss ${task.fileName}`}
                      onClick={() => removeTask(task.taskId)}
                      className="rounded p-0.5 text-text-dim hover:bg-accent-bg hover:text-text"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {task.status !== "skipped" && (
                  <Progress
                    value={pct(task)}
                    className={
                      task.status === "done"
                        ? "mt-1 h-1 [&_[data-slot=progress-indicator]]:bg-ok"
                        : task.status === "error"
                          ? "mt-1 h-1 [&_[data-slot=progress-indicator]]:bg-danger"
                          : "mt-1 h-1"
                    }
                  />
                )}
                {task.status === "skipped" && task.error && (
                  <p
                    className="mt-0.5 truncate text-[11px] text-text-dim"
                    title={task.error}
                  >
                    {task.error}
                  </p>
                )}
                {task.status === "error" && task.error && (
                  <p
                    className="mt-1 truncate text-[11px] text-danger"
                    title={task.error}
                  >
                    {task.error}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
